import React, { useCallback, useEffect, useMemo } from "react";

import { buildPasswordCredentialFormatRules } from "v3/credentialFormatRules";
import { connect } from "react-redux";
import { compose } from "redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryPassAndUserpassStep3 } from "@ui-kit/components/index.es";
import withExchangeToken from "pages/_components/withExchangeToken";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import * as i18n from "util/i18n";
import Yup from "yup";
import * as configUtils from "util/config";

function RecoveryPassAndUserpassStep3V3({
    dispatch,
    history,
    exchangeToken,
    resetCode,
    recoveryAsyncPending,
    fieldErrors,
}) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryPassword"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const minLength = configUtils.getInteger("core.password.minLength", 8);
    const maxLength = configUtils.getInteger("core.password.maxLength", 20);

    const initialValues = useMemo(
        () => ({
            newPassword: "",
            newPasswordConfirmation: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                newPassword: Yup.string()
                    .required(i18n.get("settings.changePassword.password.required"))
                    .min(minLength, i18n.get("settings.changePassword.modify.error.minLength"))
                    .max(maxLength, i18n.get("settings.changePassword.modify.error.maxLength"))
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.updateuserpassword.pattern")),
                        i18n.get("settings.changePassword.modify.error.mustBeAlphanumeric"),
                    ),
                newPasswordConfirmation: Yup.string()
                    .required(i18n.get("settings.changePassword.newPassword.required"))
                    .oneOf(
                        [Yup.ref("newPassword")],
                        i18n.get("settings.changePassword.modify.error.passwordMustMatch"),
                    ),
            }),
        [minLength, maxLength],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const newPassword = values.newPassword ?? "";
            const newPasswordConfirmation = values.newPasswordConfirmation ?? "";

            const enhancedBag = {
                ...formikBag,
                props: {
                    ...(formikBag.props || {}),
                    location: { pathname: "/v3/recoveryPassAndUserpass/step3" },
                    history,
                },
            };

            dispatch(
                recoveryPasswordActions.recoveryPassAndUserpassStep3(
                    newPassword,
                    newPasswordConfirmation,
                    exchangeToken,
                    resetCode,
                    enhancedBag,
                ),
            );
        },
        [dispatch, exchangeToken, resetCode, history],
    );

    const getLabels = useCallback((id) => {
        const keys = {
            newPassword: "recoveryPassAndUserpass.step3.newPassword.label",
            newPasswordConfirmation: "recoveryPassAndUserpass.step3.newPasswordConfirmation.label",
            submit: "global.confirm",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback((id) => i18n.get(id), []);

    const credentialFormatRuleItems = useMemo(() => buildPasswordCredentialFormatRules(minLength, maxLength), [
        minLength,
        maxLength,
    ]);

    return (
        <RecoveryPassAndUserpassStep3
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            getLabels={getLabels}
            getText={getText}
            submitLoading={recoveryAsyncPending}
            fieldErrors={fieldErrors ?? undefined}
            credentialFormatRuleItems={credentialFormatRuleItems}
        />
    );
}

RecoveryPassAndUserpassStep3V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    exchangeToken: string,
    resetCode: string,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
};

RecoveryPassAndUserpassStep3V3.defaultProps = {
    exchangeToken: null,
    resetCode: null,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
};

const mapStateToProps = (state) => ({
    exchangeToken: recoveryPasswordSelectors.getExchangeToken(state),
    resetCode: recoveryPasswordSelectors.getResetCode(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
});

export default compose(connect(mapStateToProps), withExchangeToken)(RecoveryPassAndUserpassStep3V3);
